/**
 * Frontend HTTP client for /api/checkpoints.
 *
 * The frontend derives a Checkpoint locally (services/checkpoint.ts) and
 * persists it server-side via this client. Server-side persistence
 * unlocks two flows the localStorage-only version couldn't reach:
 *   - Cross-device resume: open the same conversation on a different
 *     machine and click ↪ — the primer comes from the server.
 *   - Auto-save at the cliff: when context usage crosses ~85%, the next
 *     turn is at risk. We save a checkpoint immediately so a 413 doesn't
 *     cost the user any state.
 *
 * All methods are best-effort. The UI never blocks on these — if the
 * checkpoint endpoint is unreachable, the user still has the
 * localStorage-only fallback (Conversations panel + ↪).
 */
import type { Checkpoint } from '../types';
import { getBootstrapToken } from './sso';

const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:3001';

// Mirror of frontend/src/services/chat.ts auth pattern — real Office SSO via
// getBootstrapToken(). Previously this returned the literal string
// 'DEV_PLACEHOLDER_TOKEN', which production accepted as a bypass when
// ALLOW_DEV_TOKEN=true was left set on the ECS task. Closed 2026-05-16.
async function getAuthToken(): Promise<string> {
  return getBootstrapToken();
}

export interface CheckpointListItem {
  conversationId: string;
  sourceConversationTitle: string;
  intent: string;
  lastStatus: string;
  sourceUpdatedAt: string;
  savedAt: string;
  expiresAt: string;
  workbookFileName: string | null;
  messageCount: number;
}

export interface PutCheckpointResult {
  conversationId: string;
  savedAt: string;
  expiresAt: string;
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAuthToken();
  return fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
    credentials: 'include',  // Move 5 follow-up Step 14 — ALB stickiness cookie
  });
}

export async function putCheckpoint(
  conversationId: string,
  checkpoint: Checkpoint,
  sourceUpdatedAt: string,
): Promise<PutCheckpointResult | null> {
  try {
    const resp = await authedFetch('/api/checkpoints', {
      method: 'POST',
      body: JSON.stringify({ conversationId, checkpoint, sourceUpdatedAt }),
    });
    if (!resp.ok) {
      // Best-effort. Log but don't throw — the local copy is still good.
      console.warn(`[checkpointApi] put failed ${resp.status}`);
      return null;
    }
    return (await resp.json()) as PutCheckpointResult;
  } catch (e) {
    console.warn('[checkpointApi] put network error', (e as Error).message);
    return null;
  }
}

export async function listCheckpoints(): Promise<CheckpointListItem[]> {
  try {
    const resp = await authedFetch('/api/checkpoints', { method: 'GET' });
    if (!resp.ok) return [];
    const body = (await resp.json()) as { items?: CheckpointListItem[] };
    return body.items ?? [];
  } catch {
    return [];
  }
}

export async function fetchCheckpoint(conversationId: string): Promise<Checkpoint | null> {
  try {
    const resp = await authedFetch(`/api/checkpoints/${encodeURIComponent(conversationId)}`, {
      method: 'GET',
    });
    if (!resp.ok) return null;
    const body = (await resp.json()) as { checkpoint?: Checkpoint };
    return body.checkpoint ?? null;
  } catch {
    return null;
  }
}

export async function deleteCheckpoint(conversationId: string): Promise<boolean> {
  try {
    const resp = await authedFetch(`/api/checkpoints/${encodeURIComponent(conversationId)}`, {
      method: 'DELETE',
    });
    return resp.ok;
  } catch {
    return false;
  }
}
